import { SelectOption } from '@framework/form';
import { Select } from 'antd';
import type { SelectProps } from 'antd/es/select';
import { groupBy } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { Datatype } from '../../datatype/Datatype';
import { IReference } from '../IReference';
import { ReferenceBadge } from '../display/ReferenceBadge';

export interface IExpressionReferenceInputProps extends SelectProps {
  /**
   * Riferimenti disponibili per il widget corrente.
   */
  references: IReference[];
  name: string;
  width?: string;

  /**
   * Impostazioni extra per la select
   */
  config?: {
    /**
     * Filtra le reference in base a una condizione fornita
     */
    filter?: (referece: IReference) => boolean;
    /**
     * Se true, include le reference con datatype Empty (e.g. subsection)
     * nella lista. Altrimenti non le include.
     * Il check è fatto _dopo_ la filter, ed è valutato solo se la filter
     * è definita e torna `false`.
     */
    includeEmpty?: boolean;
  };
}

/**
 * Permette di selezionare un riferimento ad un'altro widget a partire
 * dall'elenco dei riferimenti disponibili per il widget corrente.
 */
export function ExpressionReferenceInput(
  props: IExpressionReferenceInputProps,
) {
  const { name, references, width, config, ...inputProps } = props;
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({ name });

  const options = useMemo(() => {
    const options = references
      .filter(r => {
        if (r.label == null) return false;
        if (config?.filter && !config.filter(r)) return false;
        if (!config?.includeEmpty && r.field.type === Datatype.Empty)
          return false;
        return true;
      })
      .map(reference => ({
        reference,
        group: reference.group ?? 'Riferimenti',
        value: reference.id,
        label: <ReferenceBadge reference={reference} />,
        rawLabel: reference.code,
      }));

    const grouped = groupBy(options, 'group');

    return Object.entries(grouped).map(([group, options]) => ({
      label: group,
      options: options.sort((a, b) =>
        a.reference.label.localeCompare(b.reference.label),
      ),
    }));
  }, [config, references]);

  const handleChange = useCallback(
    (value: any, option: SelectOption) => {
      onChange((option as any)?.reference ?? null);
    },
    [onChange],
  );

  // TODO Gestire il caso in cui il riferimento non è più disponibile
  const parsedValue = useMemo(
    () => references.find(r => r.id === value?.id) ?? null,
    [references, value?.id],
  );

  return (
    <Select
      placeholder="Campo..."
      {...inputProps}
      ref={ref}
      showSearch
      onChange={handleChange as any}
      onBlur={onBlur}
      value={parsedValue?.id}
      style={{ width: width ?? 'auto', minWidth: '100px' }}
      popupMatchSelectWidth={false}
      // dropdownAlign={{ offset: [0, 0] }}
      optionFilterProp="rawLabel"
      optionLabelProp="label"
      options={options}
      allowClear
    />
  );
}
